import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Megaphone } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { API } from "../App";

const EMPTY = { company_name: "", contact_name: "", email: "", phone: "", budget: "", placement: "", message: "" };

const Field = ({ label, children }) => (
  <div>
    <Label className="text-zinc-300">{label}</Label>
    {children}
  </div>
);

export const AdvertiseForm = ({ testIdPrefix = "advertise" }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const t = (k) => `${testIdPrefix}-${k}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.email || !form.contact_name) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact/advertise`, form);
      toast.success("Advertising inquiry sent! Our team will contact you shortly.");
      setForm(EMPTY);
    } catch {
      toast.error("Failed to send inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={t("form")}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Company Name *">
          <Input value={form.company_name} onChange={(e) => set("company_name")(e.target.value)} placeholder="Your company" className="mt-2 bg-zinc-800 border-zinc-700" required data-testid={t("company")} />
        </Field>
        <Field label="Contact Name *">
          <Input value={form.contact_name} onChange={(e) => set("contact_name")(e.target.value)} placeholder="Your name" className="mt-2 bg-zinc-800 border-zinc-700" required data-testid={t("contact-name")} />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Email *">
          <Input type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} placeholder="your@email.com" className="mt-2 bg-zinc-800 border-zinc-700" required data-testid={t("email")} />
        </Field>
        <Field label="Phone">
          <Input value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="+27 XX XXX XXXX" className="mt-2 bg-zinc-800 border-zinc-700" data-testid={t("phone")} />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Monthly Budget">
          <Select value={form.budget} onValueChange={set("budget")}>
            <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700" data-testid={t("budget")}><SelectValue placeholder="Select budget range" /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="under-5k">Under R5,000</SelectItem>
              <SelectItem value="5k-10k">R5,000 - R10,000</SelectItem>
              <SelectItem value="10k-25k">R10,000 - R25,000</SelectItem>
              <SelectItem value="25k-50k">R25,000 - R50,000</SelectItem>
              <SelectItem value="over-50k">Over R50,000</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Ad Placement Interest">
          <Select value={form.placement} onValueChange={set("placement")}>
            <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700" data-testid={t("placement")}><SelectValue placeholder="Select placement" /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="homepage">Homepage Banner</SelectItem>
              <SelectItem value="category">Category Pages</SelectItem>
              <SelectItem value="search">Search Results</SelectItem>
              <SelectItem value="sidebar">Sidebar Ads</SelectItem>
              <SelectItem value="newsletter">Email Newsletter</SelectItem>
              <SelectItem value="multiple">Multiple Placements</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Tell us about your advertising goals">
        <Textarea value={form.message} onChange={(e) => set("message")(e.target.value)} placeholder="What would you like to promote? Who is your target audience?" className="mt-2 min-h-32 bg-zinc-800 border-zinc-700" data-testid={t("message")} />
      </Field>
      <Button type="submit" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-500" data-testid={t("submit")}>
        {loading ? "Sending..." : "Submit Inquiry"}
        <Megaphone className="h-4 w-4 ml-2" />
      </Button>
    </form>
  );
};
